import { forwardRef, useEffect, useState } from 'react'
import classNames from 'classnames'

const WIDTH = 320
const MAX_VALUE = 1

type Props = Omit<React.HTMLProps<HTMLDivElement>, 'children'> & {
  value: number
  showPercentage?: boolean
  percentagePosition?: 'right' | 'bellow' | 'tooltip'
  colors?: {
    background?: string
    bar?: string
    barFull?: string
  }
}

/**
 * @group Components
 * @category Component
 */
export const ProgressBar = forwardRef(
  (
    { className, value, showPercentage, percentagePosition = 'right', colors, ...restProps }: Props,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const [actualValue, setActualValue] = useState<number>(0)
    const backgroundColor = colors?.background ? `bg-[${colors.background}]` : 'bg-neutral-100'
    const barColor = colors?.bar ? `bg-[${colors.bar}]` : 'bg-primary'
    const barFullColor = colors?.bar ? `bg-[${colors.barFull}]` : 'bg-positive'

    useEffect(() => {
      setActualValue(value > MAX_VALUE ? 1 : value)
    }, [value])

    return (
      <div ref={ref} className={classNames('flex flex-row', className)} {...restProps}>
        <div
          className={classNames(
            'flex',
            percentagePosition === 'right' ? 'flex-row items-center' : 'flex-col items-end gap-1'
          )}
        >
          <div className={classNames('h-1 w-[320px] flex-1')}>
            {showPercentage && percentagePosition === 'tooltip' && (
              <div
                className={classNames(
                  'relative mb-[10px] flex h-[42] w-[54px] justify-center self-center rounded-lg px-3 py-2 shadow-lg',
                  "before:absolute before:bottom-[-4px] before:left-1/2 before:block before:h-[12px] before:w-[12px] before:-translate-x-1/2 before:rotate-45 before:transform before:bg-white before:content-['']"
                )}
                style={{ left: `${actualValue * WIDTH - 27}px` }}
              >{`${Math.floor(value * 100)}%`}</div>
            )}
            <div className={classNames('h-1 flex-1 rounded bg-neutral-100', backgroundColor)} />
            <div
              className={classNames(
                'absolute -mt-1 h-1 min-w-2 max-w-prose flex-1 rounded transition-width duration-500',
                value >= 1 ? barFullColor : barColor
              )}
              style={{ width: `${actualValue * WIDTH}px` }}
            />
          </div>
          {showPercentage && percentagePosition !== 'tooltip' && (
            <label className="ml-3 text-sm text-neutral-900">{`${Math.floor(value * 100)}%`}</label>
          )}
        </div>
      </div>
    )
  }
)

ProgressBar.displayName = 'ProgressBar'
